import { useState } from "react";
import InlineEditableField from "../InlineEditableField";
import { SOCIAL_KEYS } from "../../lib/connectionDetails";

const SOCIAL_LABELS = {
  linkedin: "LinkedIn",
  instagram: "Instagram",
  x: "X",
  website: "Website",
};

function formatPhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return raw || "";
  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
}

function formatBirthday(value) {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function Row({ label, children }) {
  return (
    <div className="contact-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * Contact details as a definition list in the identity rail.
 *
 * Location is read and written through the Locations list rather than a second
 * copy of the value, so the rail and the Locations section never disagree.
 */
export default function ContactList({
  email,
  emailIsPublished,
  phone,
  birthday,
  locations = [],
  socials = {},
  onChangeEmail,
  onChangePhone,
  onChangeBirthday,
  onChangeLocations,
  onChangeSocials,
}) {
  const [pickingSocial, setPickingSocial] = useState(false);
  // A social is only stored once it has a URL — normalizeConnectionDetails
  // drops empty values, so an unfinished entry lives in local state.
  const [pendingSocial, setPendingSocial] = useState(null);

  const currentLocation = locations.find((item) => item.current) || locations[0] || null;
  const unusedSocials = SOCIAL_KEYS.filter(
    (key) => !socials[key] && key !== pendingSocial
  );

  const saveLocation = (label) => {
    if (!label) {
      if (currentLocation) {
        onChangeLocations(locations.filter((item) => item.id !== currentLocation.id));
      }
      return;
    }
    if (currentLocation) {
      onChangeLocations(
        locations.map((item) => (item.id === currentLocation.id ? { ...item, label } : item))
      );
      return;
    }
    onChangeLocations([...locations, { label, current: true }]);
  };

  return (
    <dl className="contact-list">
      <Row label="Email">
        {emailIsPublished ? (
          <span className="contact-static" title="From their Aline profile">{email}</span>
        ) : (
          <InlineEditableField
            value={email || ""}
            label="email"
            type="email"
            placeholder="Add email"
            onSave={onChangeEmail}
          />
        )}
      </Row>

      <Row label="Phone">
        <InlineEditableField
          value={phone || ""}
          label="phone number"
          type="tel"
          placeholder="Add phone"
          onSave={onChangePhone}
        >
          <span className="inline-edit-value">{phone ? formatPhone(phone) : "Add phone"}</span>
        </InlineEditableField>
      </Row>

      <Row label="Lives in">
        <InlineEditableField
          value={currentLocation?.label || ""}
          label="location"
          placeholder="Add location"
          onSave={saveLocation}
        />
      </Row>

      <Row label="Birthday">
        <InlineEditableField
          value={birthday || ""}
          label="birthday"
          type="date"
          placeholder="Add birthday"
          onSave={onChangeBirthday}
        >
          <span className="inline-edit-value">
            {birthday ? formatBirthday(birthday) : "Add birthday"}
          </span>
        </InlineEditableField>
      </Row>

      {SOCIAL_KEYS.filter((key) => socials[key]).map((key) => (
        <Row key={key} label={SOCIAL_LABELS[key]}>
          <span className="contact-social">
            <a href={socials[key]} target="_blank" rel="noreferrer noopener">
              Open
            </a>
            <InlineEditableField
              value={socials[key]}
              label={SOCIAL_LABELS[key]}
              placeholder={`${SOCIAL_LABELS[key]} URL`}
              onSave={(next) => onChangeSocials({ ...socials, [key]: next })}
            >
              <span className="visually-hidden">Edit {SOCIAL_LABELS[key]}</span>
            </InlineEditableField>
          </span>
        </Row>
      ))}

      {pendingSocial && (
        <Row label={SOCIAL_LABELS[pendingSocial]}>
          <InlineEditableField
            value=""
            label={SOCIAL_LABELS[pendingSocial]}
            placeholder={`${SOCIAL_LABELS[pendingSocial]} URL`}
            onSave={(next) => {
              setPendingSocial(null);
              if (next) onChangeSocials({ ...socials, [pendingSocial]: next });
            }}
          />
        </Row>
      )}

      {unusedSocials.length > 0 && (
        <div className="contact-row is-action">
          <dt />
          <dd>
            {pickingSocial ? (
              <span className="contact-social-picker">
                {unusedSocials.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setPendingSocial(key);
                      setPickingSocial(false);
                    }}
                  >
                    {SOCIAL_LABELS[key]}
                  </button>
                ))}
                <button type="button" className="is-cancel" onClick={() => setPickingSocial(false)}>
                  Cancel
                </button>
              </span>
            ) : (
              <button type="button" className="link-button" onClick={() => setPickingSocial(true)}>
                Add a link
              </button>
            )}
          </dd>
        </div>
      )}
    </dl>
  );
}
